import {
  Cog,
  BrainCircuit,
  Database,
  Megaphone,
  Users,
  Target,
  ShieldCheck,
  Handshake,
  BadgeCheck,
} from "lucide-react";

const disciplines = [
  {
    icon: Cog,
    name: "RevOps & CRM Architecture",
    detail: "HubSpot & Salesforce architects who redesign pipeline, lifecycle, and reporting — not just install fields.",
  },
  {
    icon: BrainCircuit,
    name: "AI & Automation Engineering",
    detail: "Copilots, agents and workflow orchestration built on top of your data — production-grade, not demoware.",
  },
  {
    icon: Database,
    name: "Data & Analytics",
    detail: "Warehouse design, attribution and forecasting instrumentation — the layer everything else depends on.",
  },
  {
    icon: Megaphone,
    name: "Marketing Systems & Content",
    detail: "Campaign infrastructure, sequencing, and content ops — capable of the volume an AI-native motion produces.",
  },
  {
    icon: Users,
    name: "Sales Enablement & Coaching",
    detail: "Playbooks, deal-desk process, rep coaching cadences — the human side of a systematised motion.",
  },
  {
    icon: Target,
    name: "Positioning & Product Marketing",
    detail: "ICP work, category positioning, and launch strategy that survive contact with the market.",
  },
];

const principles = [
  {
    icon: BadgeCheck,
    k: "Vetted",
    v: "Every partner has delivered inside an engagement Chris has led — reputation costs are real, so the bar is high.",
  },
  {
    icon: Handshake,
    k: "Matched",
    v: "Introductions only when the strategy calls for it. No retainers on autopilot, no bundled agency deals.",
  },
  {
    icon: ShieldCheck,
    k: "Standards-locked",
    v: "Partners work to a shared operating standard set by Chris — same cadence, same quality bar, same reporting.",
  },
];

export const Partners = () => {
  return (
    <section
      id="partners"
      data-testid="partners-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div
        className="absolute inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(ellipse 55% 45% at 80% 20%, rgba(124,58,237,0.12), transparent 70%)",
        }}
        aria-hidden
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-xs font-mono-ui uppercase tracking-[0.28em] text-violet-400 mb-4">
          ─── Implementation Partners
        </div>

        {/* Pull quote header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start mb-14">
          <div className="md:col-span-8">
            <div className="relative">
              <span
                aria-hidden
                className="absolute -left-2 -top-6 font-display text-7xl md:text-8xl text-violet-500/25 leading-none select-none"
              >
                “
              </span>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter text-zinc-50 leading-[1.05] relative">
                Strategy only sings loudly upon{" "}
                <span className="text-violet-300">successful execution.</span>
              </h2>
            </div>
          </div>
          <div className="md:col-span-4 md:pt-4">
            <p className="text-sm md:text-base text-zinc-400 leading-relaxed">
              Catlyz is advisory. Execution belongs to your team — and, when
              the work calls for it, to a small circle of specialists Chris
              has spent years working alongside.
            </p>
            <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
              Introduced on request. Vetted on outcomes. Held to the standard
              Chris's name sits behind.
            </p>
          </div>
        </div>

        {/* Discipline grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5 mb-16">
          {disciplines.map((d) => {
            const Icon = d.icon;
            return (
              <div
                key={d.name}
                data-testid={`partner-discipline-${d.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-zinc-950 p-7 md:p-8 flex flex-col card-hover"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="inline-flex h-10 w-10 items-center justify-center border border-white/10 group-hover:border-violet-400/40 transition-colors">
                    <Icon className="h-4 w-4 text-violet-300" strokeWidth={1.5} />
                  </span>
                  <span className="text-[10px] font-mono-ui uppercase tracking-[0.24em] text-zinc-600">
                    Specialist
                  </span>
                </div>
                <div className="font-display text-lg text-zinc-50 leading-snug mb-2">
                  {d.name}
                </div>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  {d.detail}
                </p>
              </div>
            );
          })}
        </div>

        {/* Principles strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {principles.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.k}
                data-testid={`partner-principle-${p.k.toLowerCase()}`}
                className="border border-white/10 bg-zinc-950/60 p-6"
              >
                <div className="flex items-center gap-3">
                  <Icon className="h-4 w-4 text-violet-300" strokeWidth={1.6} />
                  <span className="text-[11px] font-mono-ui uppercase tracking-[0.24em] text-violet-300">
                    {p.k}
                  </span>
                </div>
                <p className="mt-4 text-sm text-zinc-300 leading-relaxed">
                  {p.v}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
